import PackageModel, { PackagesMap } from '@geartrack/shared-lib/dist/PackageModel'
import { deleteField, doc, Firestore, runTransaction, setDoc, updateDoc } from 'firebase/firestore'
import { IPackagesRepository } from './packages'
import { mapObject } from '@geartrack/shared-lib/dist/utils'

export class PackagesRepositoryFirebase implements IPackagesRepository {
  constructor(private readonly userId: string, private readonly firestore: Firestore) {}

  addPackage(pkg: PackageModel) {
    return this.upsertPackage(pkg)
  }

  updatePackage(pkg: PackageModel, changes: Partial<PackageModel>) {
    return this.upsertPackage(pkg.edit(changes))
  }

  toggleCollapsePackage(pkg: PackageModel) {
    return this.upsertPackage(pkg.edit({ collapsed: !pkg.collapsed }))
  }

  addSortFieldLastStateDate(pkg: PackageModel, dateInMs: number) {
    const updated = pkg.updateOrder(dateInMs)

    if (updated !== pkg) {
      // new reference means the value was updated
      // we need to save it
      return this.upsertPackage(updated)
    }
  }

  getDocRef() {
    return doc(this.firestore, 'users', this.userId)
  }

  upsertPackage(pkg: PackageModel) {
    const docRef = this.getDocRef()

    return updateDoc(docRef, {
      packages: {
        [pkg.computedKey]: pkg.serializeToFirestoreDocument(),
      },
    })
  }

  removePackage(pkg: PackageModel): Promise<void> {
    const docRef = this.getDocRef()

    return setDoc(
      docRef,
      {
        packages: {
          [pkg.computedKey]: deleteField(),
        },
      },
      { merge: true }
    )
  }

  /**
   * Merge packages arrays
   * Usually used to merge local and remote packages
   *
   * We give the remote packages' priority because they could be changed by another instance
   */
  async synchronizePackages(localPackages: PackagesMap): Promise<PackagesMap> {
    return runTransaction(this.firestore, async transaction => {
      const userDoc = await transaction.get(this.getDocRef())

      if (!userDoc.exists()) {
        const serializedMap = mapObject<PackageModel>(localPackages, pkg => pkg.serializeToFirestoreDocument())

        // store the local packages on the server
        transaction.set(
          this.getDocRef(),
          {
            packages: serializedMap,
          },
          { merge: true }
        )

        return localPackages
      }

      const data = userDoc.data()
      let remotePackages: PackagesMap = {}

      if (data.packages) {
        // map from firestore format to PackageModel
        remotePackages = mapObject<PackageModel, PackageModel>(data.packages, pkg =>
          PackageModel.fromFirestoreObject(pkg)
        )
      }

      const mergeResult = mergePackagesResult(localPackages, remotePackages)

      transaction.update(this.getDocRef(), {
        packages: mapObject<PackageModel>(mergeResult, pkg => pkg.serializeToFirestoreDocument()),
      })

      return mergeResult
    })
  }
}

/**
 * Merge packages arrays
 * Usually used to merge local and remote packages
 *
 * We give the remote packages' priority because they could be changed by another instance
 * @param leftPackages
 * @param rightPackages
 */
export const mergePackagesResult = (leftPackages: PackagesMap, rightPackages: PackagesMap): PackagesMap => {
  const mergedPackages = Object.assign({}, leftPackages)

  // update existing ones or add new ones
  for (let key in rightPackages) {
    mergedPackages[key] = rightPackages[key]
  }

  return mergedPackages
}
